import {useCallback, useState} from 'react';

import {isEmpty} from 'lodash';

import {showSnackbar} from '@/app/shared-components/Snackbar/SnackbarProvider';
import {HTTP_ERROR} from '@/constants/HttpErrors';
import {IPaymentLinkV2TrackApi} from '@/dataSource/src/api/payment-link/entities';
import {usePaymentLinkTrackers} from '@/dataSource/src/api/payment-link/trackers';
import {ErrorManager} from '@/utils/errors/errorManager';
import {extractHttpStatus} from '@/utils/errors/extractHttpStatus';
import logError from '@/utils/errors/logError';

import {
  useCreatePaymentSessionMutation,
  useLazyGetDefaultPaymentLinkByUserIdQuery,
  useLazyGetLimitFeesQuery,
  useLazyGetPaymentLinkByIdQuery,
  useSubmitApplePayPaymentMutation,
  useSubmitPaymentSessionMutation,
  useValidateApplePayMerchantMutation,
} from './api';
import {
  IApplePayMerchantSession,
  IApplePayPaymentRequest,
  IApplePaySubmitResponse,
  ICreatePaymentSessionRequest,
  ICreatePaymentSessionResponse,
  IGetPaymentLinkV2Response,
  ILimitsFeesResponse,
  ISubmitPaymentSessionModel,
  ISubmitPaymentSessionResponse,
} from './models';

const ERROR_TITLE_FEES = "We couldn't load limits & fees";
const ERROR_TITLE_PAYMENT_LINK = "We couldn't load the payment link";
const ERROR_TITLE_SESSION = "We couldn't create the payment session";
const ERROR_TITLE_SUBMIT = "We couldn't submit the payment";
const ERROR_MESSAGE = 'Please try again in a moment.';

export const usePaymentLinkV2Repository = () => {
  const [limitsFees, setLimitsFees] = useState<ILimitsFeesResponse | undefined>(
    undefined,
  );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_paymentLink, setPaymentLink] = useState<
    IGetPaymentLinkV2Response | undefined
  >(undefined);

  const [
    triggerGetLimitsFees,
    {isLoading: isGetLimitsFeesLoading, isError: isGetLimitsFeesError},
  ] = useLazyGetLimitFeesQuery();

  const [
    triggerGetPaymentLinkById,
    {isLoading: isGetPaymentLinkLoading, isError: isGetPaymentLinkError},
  ] = useLazyGetPaymentLinkByIdQuery();

  const [
    triggerGetDefaultPaymentLinkByUserId,
    {
      isLoading: isGetDefaultPaymentLinkByUserIdLoading,
      isError: isGetDefaultPaymentLinkByUserIdError,
    },
  ] = useLazyGetDefaultPaymentLinkByUserIdQuery();

  const [
    triggerCreatePaymentSession,
    {
      isLoading: isCreatingPaymentSessionLoading,
      isError: isCreatingPaymentSessionError,
    },
  ] = useCreatePaymentSessionMutation();

  const [
    triggerSubmitPaymentSession,
    {
      isLoading: isSubmittingPaymentSessionLoading,
      isError: isSubmittingPaymentSessionError,
    },
  ] = useSubmitPaymentSessionMutation();

  const [triggerValidateApplePayMerchant] =
    useValidateApplePayMerchantMutation();

  const [triggerSubmitApplePayPayment] = useSubmitApplePayPaymentMutation();

  const {
    trackApiRequestMoneyCreateSessionSuccess,
    trackApiRequestMoneyCreateSessionError,
    trackApiRequestMoneyPaidSuccess,
    trackApiRequestMoneyPaidError,
    trackApiPaymentLinkGetInformationSuccess,
    trackApiPaymentLinkGetInformationError,
  } = usePaymentLinkTrackers();

  const handlerGetLimitsFees = useCallback(async () => {
    try {
      const response = await triggerGetLimitsFees().unwrap();
      if (isEmpty(response)) {
        const err = new Error('Limits & fees empty response)');
        logError(err, {
          customTitle: 'PaymentLinkV2 Limits & fees empty response',
          fileName: 'usePaymentLinkV2Repository',
          functionName: 'handlerGetLimitsFees',
        });
        showSnackbar({summary: ERROR_TITLE_FEES, message: ERROR_MESSAGE});
        return null;
      }

      setLimitsFees(response);
      return response;
    } catch (error) {
      logError(error, {
        customTitle: 'PaymentLinkV2 Limits & fees error',
        fileName: 'usePaymentLinkV2Repository',
        functionName: 'handlerGetLimitsFees',
      });
      ErrorManager.handleError(error);
      throw error;
    }
  }, [triggerGetLimitsFees]);

  const clearLimitsFees = useCallback(() => {
    setLimitsFees(undefined);
  }, []);

  const handlerGetPaymentLinkById = useCallback(
    async (
      id: string,
      type?: string,
    ): Promise<IGetPaymentLinkV2Response | null> => {
      try {
        const response = await triggerGetPaymentLinkById({id, type}).unwrap();
        if (isEmpty(response)) {
          const err = new Error('Payment link empty response');
          trackApiPaymentLinkGetInformationError(err);
          logError(err, {
            customTitle: 'PaymentLinkV2 getLink empty response',
            fileName: 'usePaymentLinkV2Repository',
            functionName: 'handlerGetPaymentLinkById',
            extraTags: {
              payment_link_id: id,
            },
          });
          showSnackbar({
            summary: ERROR_TITLE_PAYMENT_LINK,
            message: ERROR_MESSAGE,
          });
          return null;
        }
        trackApiPaymentLinkGetInformationSuccess();
        setPaymentLink(response);
        return response;
      } catch (error) {
        const httpCode = extractHttpStatus(error);
        if (httpCode === HTTP_ERROR.BAD_REQUEST) {
          logError(error, {
            fileName: 'usePaymentLinkV2Repository',
            functionName: 'handlerGetPaymentLinkById',
            extraTags: {payment_link_id: id},
            customTitle: 'PaymentLinkV2 link already used ?',
          });
          throw error;
        }
        trackApiPaymentLinkGetInformationError(error);
        logError(error, {
          customTitle: 'PaymentLinkV2 getLink error',
          fileName: 'usePaymentLinkV2Repository',
          functionName: 'handlerGetPaymentLinkById',
          extraTags: {
            payment_link_id: id,
          },
        });
        throw error;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [triggerGetPaymentLinkById],
  );

  const handlerCreatePaymentSession = useCallback(
    async (
      payload: ICreatePaymentSessionRequest,
      trackApiProps: IPaymentLinkV2TrackApi,
    ): Promise<ICreatePaymentSessionResponse | null> => {
      try {
        const response = await triggerCreatePaymentSession(payload).unwrap();
        if (isEmpty(response)) {
          const err = new Error('Create Payment session empty response');
          trackApiRequestMoneyCreateSessionError(trackApiProps, err);
          logError(err, {
            customTitle: 'Payment link empty response',
            fileName: 'usePaymentLinkV2Repository',
            functionName: 'handlerCreatePaymentSession',
            data: payload,
          });
          showSnackbar({summary: ERROR_TITLE_SESSION, message: ERROR_MESSAGE});

          return null;
        }
        trackApiRequestMoneyCreateSessionSuccess(trackApiProps);
        return response;
      } catch (error) {
        trackApiRequestMoneyCreateSessionError(trackApiProps, error);
        logError(error, {
          customTitle: 'PaymentLinkV2 create payment session error',
          fileName: 'usePaymentLinkV2Repository',
          functionName: 'handlerCreatePaymentSession',
          data: payload,
        });
        ErrorManager.handleError(error);
        return null;
      }
    },
    [
      trackApiRequestMoneyCreateSessionError,
      trackApiRequestMoneyCreateSessionSuccess,
      triggerCreatePaymentSession,
    ],
  );

  const handlerSubmitPaymentSession = useCallback(
    async (
      payload: ISubmitPaymentSessionModel,
      trackApiProps: IPaymentLinkV2TrackApi,
    ): Promise<ISubmitPaymentSessionResponse | null> => {
      try {
        const response = await triggerSubmitPaymentSession(payload).unwrap();
        if (isEmpty(response)) {
          const err = new Error('Submit Payment session empty response');
          trackApiRequestMoneyPaidError(trackApiProps, err);
          logError(err, {
            customTitle: 'PaymentLinkV2 submit payment session empty response',
            fileName: 'usePaymentLinkV2Repository',
            functionName: 'handlerSubmitPaymentSession',
            data: payload,
          });
          showSnackbar({summary: ERROR_TITLE_SUBMIT, message: ERROR_MESSAGE});
          return null;
        }
        trackApiRequestMoneyPaidSuccess(trackApiProps);
        return response;
      } catch (error) {
        trackApiRequestMoneyPaidError(trackApiProps, error);
        logError(error, {
          customTitle: 'PaymentLinkV2 submit payment session error',
          fileName: 'usePaymentLinkV2Repository',
          functionName: 'handlerSubmitPaymentSession',
          data: payload,
          extraTags: {
            payment_session_id: payload.payment_session_id,
          },
        });
        ErrorManager.handleError(error);
        return null;
      }
    },
    [
      trackApiRequestMoneyPaidError,
      trackApiRequestMoneyPaidSuccess,
      triggerSubmitPaymentSession,
    ],
  );

  const handlerValidateApplePayMerchant =
    useCallback(async (): Promise<IApplePayMerchantSession> => {
      try {
        const response = await triggerValidateApplePayMerchant().unwrap();
        return response;
      } catch (error) {
        logError(error, {
          customTitle: 'ApplePay validate merchant error',
          fileName: 'usePaymentLinkV2Repository',
          functionName: 'handlerValidateApplePayMerchant',
        });
        throw error;
      }
    }, [triggerValidateApplePayMerchant]);

  const handlerSubmitApplePayPayment = useCallback(
    async (
      payload: IApplePayPaymentRequest,
      trackApiProps: IPaymentLinkV2TrackApi,
    ): Promise<IApplePaySubmitResponse | null> => {
      try {
        const response = await triggerSubmitApplePayPayment(payload).unwrap();
        if (isEmpty(response)) {
          const err = new Error('Apple Pay submit payment empty response');
          trackApiRequestMoneyPaidError(trackApiProps, err);
          logError(err, {
            customTitle: 'ApplePay submit payment empty response',
            fileName: 'usePaymentLinkV2Repository',
            functionName: 'handlerSubmitApplePayPayment',
            data: payload,
            extraTags: {payment_link_id: payload.payment_link_id},
          });
          return null;
        }
        trackApiRequestMoneyPaidSuccess(trackApiProps);
        return response;
      } catch (error) {
        trackApiRequestMoneyPaidError(trackApiProps, error);
        logError(error, {
          customTitle: 'ApplePay submit payment error',
          fileName: 'usePaymentLinkV2Repository',
          functionName: 'handlerSubmitApplePayPayment',
          data: payload,
          extraTags: {payment_link_id: payload.payment_link_id},
        });
        ErrorManager.handleError(error);
        return null;
      }
    },
    [
      trackApiRequestMoneyPaidError,
      trackApiRequestMoneyPaidSuccess,
      triggerSubmitApplePayPayment,
    ],
  );

  const handlerGetDefaultPaymentLinkByUserId = useCallback(
    async (userId: string): Promise<string | null> => {
      try {
        const response =
          await triggerGetDefaultPaymentLinkByUserId(userId).unwrap();
        if (isEmpty(response)) {
          const err = new Error('Default payment link empty response');
          logError(err, {
            customTitle:
              'PaymentLinkV2 get default link by user id empty response',
            fileName: 'usePaymentLinkV2Repository',
            functionName: 'handlerGetDefaultPaymentLinkByUserId',
            extraTags: {
              user_id: userId,
            },
          });
          return null;
        }
        return response.default_payment_link.id;
      } catch (error) {
        logError(error, {
          customTitle: 'PaymentLinkV2 get default link by user id error',
          fileName: 'usePaymentLinkV2Repository',
          functionName: 'handlerGetDefaultPaymentLinkByUserId',
          extraTags: {
            user_id: userId,
          },
        });
        return null;
      }
    },
    [triggerGetDefaultPaymentLinkByUserId],
  );

  return {
    limitsFees,
    isGetLimitsFeesLoading,
    isGetLimitsFeesError,
    handlerGetLimitsFees,
    clearLimitsFees,
    handlerGetPaymentLinkById,
    isGetPaymentLinkLoading,
    isGetPaymentLinkError,
    handlerCreatePaymentSession,
    isCreatingPaymentSessionLoading,
    isCreatingPaymentSessionError,
    handlerSubmitPaymentSession,
    isSubmittingPaymentSessionLoading,
    isSubmittingPaymentSessionError,
    handlerValidateApplePayMerchant,
    handlerSubmitApplePayPayment,
    handlerGetDefaultPaymentLinkByUserId,
    isGetDefaultPaymentLinkByUserIdLoading,
    isGetDefaultPaymentLinkByUserIdError,
  };
};
